/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ConfigModule } from "@/store/modules/config";
import { MrfiktivHttpClientProvider } from "../mrfiktiv/mrfiktiv-http-client.provider";
import { PartnerUser as MrfiktivPartnerUser } from "../mrfiktiv/v1/PartnerUser";
import {
  MrfiktivCreateUserDtoGen,
  MrfiktivPageViewModelGen,
  MrfiktivPartnerUserControllerFindAllByPartnerIdParamsGen,
  MrfiktivPartnerUserViewModelGen,
  MrfiktivPermissionDtoGen,
  MrfiktivUpdatePartnerUserDtoGen,
  MrfiktivUserIdentityViewModelGen
} from "../mrfiktiv/v1/data-contracts";
import { ThgHttpClientProvider } from "../thg/thg-http-client.provider";
import { PartnerUser as ThgPartnerUser } from "../thg/v1/PartnerUser";
import {
  ThgCreateUserDtoGen,
  ThgPartnerUserControllerFindAllByPartnerIdParamsGen,
  ThgPermissionDtoGen,
  ThgUpdatePartnerUserDtoGen
} from "../thg/v1/data-contracts";

/**
 * Communicates with `/partner/{partnerId}/user`, shared by the mrfiktiv and thg backends.
 *
 * Both backends expose the same routes, but a session is only ever authenticated against
 * one of them: calling the mrfiktiv route from a THG app answers 401, which is why every
 * method picks its proxy by app context instead of defaulting to mrfiktiv.
 */
export class PartnerUserService {
  /**
   * The proxy for the mrfiktiv backend.
   */
  mrfiktivProxy: MrfiktivPartnerUser;

  /**
   * The proxy for the thg backend.
   */
  thgProxy: ThgPartnerUser;

  /**
   * @class Initialize shared PartnerUserService
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider, thgHttpClientProvider: ThgHttpClientProvider) {
    this.mrfiktivProxy = new MrfiktivPartnerUser(mrfiktivHttpClientProvider.client());
    this.thgProxy = new ThgPartnerUser(thgHttpClientProvider.client());
  }

  /**
   * Whether the running app talks to the thg backend.
   */
  private get isThgContext(): boolean {
    return ConfigModule.appContext === AppContextEnum.THG || ConfigModule.appContext === AppContextEnum.THG_PORTAL;
  }

  async create(partnerId: string, data: MrfiktivCreateUserDtoGen): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerCreate(partnerId, data as ThgCreateUserDtoGen)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerCreate(partnerId, data)).data;
  }

  async remove(partnerId: string, userId: string): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerRemove(partnerId, userId)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerRemove(partnerId, userId)).data;
  }

  async removePermission(
    partnerId: string,
    userId: string,
    permission: MrfiktivPermissionDtoGen[]
  ): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (
        await this.thgProxy.partnerUserControllerRemovePermission(partnerId, userId, {
          permission: permission as ThgPermissionDtoGen[]
        })
      ).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerRemovePermission(partnerId, userId, { permission })).data;
  }

  async addPermissions(
    partnerId: string,
    userId: string,
    permission: MrfiktivPermissionDtoGen[]
  ): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (
        await this.thgProxy.partnerUserControllerAddPermission(partnerId, userId, {
          permission: permission as ThgPermissionDtoGen[]
        })
      ).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerAddPermission(partnerId, userId, { permission })).data;
  }

  async findOne(partnerId: string, userId: string): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerFindOneByPartnerId(partnerId, userId)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerFindOneByPartnerId(partnerId, userId)).data;
  }

  async findOneByName(partnerId: string, username: string): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerFindOneByNameAndPartnerId(partnerId, username)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerFindOneByNameAndPartnerId(partnerId, username)).data;
  }

  async getStatus(partnerId: string, userId: string): Promise<MrfiktivUserIdentityViewModelGen> {
    if (this.isThgContext) {
      // Both backends answer with the same payload; the thg spec just leaves `user.authInfo` undescribed.
      return (await this.thgProxy.partnerUserControllerGetUserStatusByPartnerId(partnerId, userId))
        .data as MrfiktivUserIdentityViewModelGen;
    }

    return (await this.mrfiktivProxy.partnerUserControllerGetUserStatusByPartnerId(partnerId, userId)).data;
  }

  async findAll(
    query: MrfiktivPartnerUserControllerFindAllByPartnerIdParamsGen
  ): Promise<MrfiktivPageViewModelGen & { data?: MrfiktivPartnerUserViewModelGen[] }> {
    if (this.isThgContext) {
      return (
        await this.thgProxy.partnerUserControllerFindAllByPartnerId(
          query as ThgPartnerUserControllerFindAllByPartnerIdParamsGen
        )
      ).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerFindAllByPartnerId(query)).data;
  }

  async invite(partnerId: string, data: MrfiktivCreateUserDtoGen): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerInvite(partnerId, data as ThgCreateUserDtoGen)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerInvite(partnerId, data)).data;
  }

  async update(
    partnerId: string,
    userId: string,
    data: MrfiktivUpdatePartnerUserDtoGen
  ): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerUpdate(partnerId, userId, data as ThgUpdatePartnerUserDtoGen))
        .data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerUpdate(partnerId, userId, data)).data;
  }

  async resendInvitation(partnerId: string, userId: string): Promise<MrfiktivPartnerUserViewModelGen> {
    if (this.isThgContext) {
      return (await this.thgProxy.partnerUserControllerResendInvitation(partnerId, userId)).data;
    }

    return (await this.mrfiktivProxy.partnerUserControllerResendInvitation(partnerId, userId)).data;
  }

  /**
   * Replaces the profile picture of a user of the partner.
   *
   * Avatars are a mrfiktiv-only feature: the thg backend exposes no avatar route,
   * so this stays on the mrfiktiv proxy in every app context.
   */
  async uploadAvatar(partnerId: string, userId: string, image: File): Promise<MrfiktivPartnerUserViewModelGen> {
    return (await this.mrfiktivProxy.partnerUserControllerUploadAvatar(partnerId, userId, { image })).data;
  }

  /**
   * Removes the profile picture of a user of the partner.
   *
   * See {@link uploadAvatar}: mrfiktiv-only.
   */
  async removeAvatar(partnerId: string, userId: string): Promise<MrfiktivPartnerUserViewModelGen> {
    return (await this.mrfiktivProxy.partnerUserControllerRemoveAvatar(partnerId, userId)).data;
  }
}

export default new PartnerUserService(new MrfiktivHttpClientProvider(), new ThgHttpClientProvider());
